import React, { useState } from "react";
import { X } from "lucide-react";
import { ReactComponent as DeleteIcon } from "../../../assects/svg/client_details/delete.svg";
import CustomDropdown from "../../Ui_Components/CustomDropdown";
import { addSupplement } from "../../../services/subPlans";
import { useAuth } from "../../../context/auth/AuthContext";
import { sendPlanNotificationToUser } from "../../../utils/oneSignalNotification";
import "../../styles/useCustomScrollbar";
import useCustomScrollbar from "../../styles/useCustomScrollbar";
import { Button } from "../../Ui_Components/Button";

interface SupplementPlanCreatePopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  userId: number;
  // ── View mode props ──
  viewMode?: boolean;
  initialData?: SupplementPlanData | null;
  onShowToast?: (
    type: "success" | "error" | "warning" | "info",
    title: string,
    message?: string,
    duration?: number
  ) => void;
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface DosageEntry {
  id: string;
  mealTime: string;
  whenToTake: string;
  supplementType: string;
  dosage: string;
  consumeHour: string;
  consumeMinute: string;
  consumePeriod: string;
}

interface SupplementEntry {
  id: string;
  name: string;
  saltName: string;
  strength: string;
  frequency: string;
  dayOfWeek: string;
  consumeWith: string;
  dosages: DosageEntry[];
}

export interface SupplementPlanData {
  id: number;
  name: string;
  created_by: string;
  created_on: string;
  status: string;
  supplements: SupplementEntry[];
}

// ── Factories ──────────────────────────────────────────────────────────────────
const newEmptyDosage = (): DosageEntry => ({
  id: `${Date.now()}-${Math.random()}`,
  mealTime: "",
  whenToTake: "",
  supplementType: "",
  dosage: "1",
  consumeHour: "",
  consumeMinute: "",
  consumePeriod: "",
});

const newEmptySupplement = (): SupplementEntry => ({
  id: `${Date.now()}-${Math.random()}`,
  name: "",
  saltName: "",
  strength: "",
  frequency: "",
  dayOfWeek: "",
  consumeWith: "",
  dosages: [newEmptyDosage()],
});

// ── Option arrays ─────────────────────────────────────────────────────────────
const frequencyOptions = [
  { value: "Daily", label: "Daily" },
  { value: "Alternate days", label: "Alternate days" },
  { value: "Weekly", label: "Weekly" },
];
  
const dayOfWeekOptions = [
  { value: "Monday", label: "Monday" },
  { value: "Tuesday", label: "Tuesday" },
  { value: "Wednesday", label: "Wednesday" },
  { value: "Thursday", label: "Thursday" },
  { value: "Friday", label: "Friday" },
  { value: "Saturday", label: "Saturday" },
  { value: "Sunday", label: "Sunday" },
];

const consumeWithTopOptions = [
  { value: "Water", label: "Water" },
  { value: "Milk", label: "Milk" },

];

const mealTimeOptions = [
   { value: "On waking up", label: "On waking up" },
   { value: "Pre breakfast", label: "Pre breakfast" },
  { value: "Breakfast", label: "Breakfast" },
  { value: "Mid-morning", label: "Mid-morning" },
  { value: "Brunch", label: "Brunch" },
  { value: "Lunch", label: "Lunch" },
  { value: "Post Lunch", label: "Post Lunch" },
  { value: "Tea time", label: "Tea time" },
  { value: "Dinner", label: "Dinner" },
  { value: "Post Dinner", label: "Post Dinner" },
  { value: "Snack", label: "Snack" },
  { value: "Bedtime", label: "Bedtime" },
];

const whenToTakeOptions = [
  { value: "Before meal", label: "Before meal" },
  { value: "After meal", label: "After meal" },

];

const supplementTypeOptions = [
  { value: "Capsules/Tablets/Soft (in no.)", label: "Capsules/Tablets/Soft (in no.)" },
  { value: "Powder (in gm)", label: "Powder (in gm)" },
  { value: "Liquid (in ml)", label: "Liquid (in ml)" },
  { value: "Gummy (in no.)", label: "Gummy (in no.)" },
  { value: "Effervescent tablet (in no.)", label: "Effervescent tablet (in no.)" },
  { value: "Patch", label: "Patch" },
];

const dosageCountOptions = Array.from({ length: 10 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const hourOptions = Array.from({ length: 13 }, (_, i) => ({
  value: String(i).padStart(2, "0"),
  label: String(i).padStart(2, "0"),
}));

const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
  value: String(i).padStart(2, "0"),
  label: String(i).padStart(2, "0"),
}));

const periodOptions = [
  { value: "AM", label: "AM" },
  { value: "PM", label: "PM" },
];

// ── Read-only field helper ────────────────────────────────────────────────────
const ReadOnlyField: React.FC<{ value: string; placeholder?: string }> = ({
  value,
  placeholder = "—",
}) => (
  <div className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base bg-[#F9F9F9] text-[#1F1F1F] min-h-[38px]">
    {value || <span className="text-[#C7C7CC]">{placeholder}</span>}
  </div>
);

// ── DosageRow ─────────────────────────────────────────────────────────────────
interface DosageRowProps {
  dosage: DosageEntry;
  totalRows: number;
  supplementId: string;
  viewMode?: boolean;
  onDosageChange: (
    supplementId: string,
    dosageId: string,
    field: string,
    value: string
  ) => void;
  onRemoveDosage: (supplementId: string, dosageId: string) => void;
}

const DosageRow: React.FC<DosageRowProps> = ({
  dosage,
  totalRows,
  supplementId,
  viewMode = false,
  onDosageChange,
  onRemoveDosage,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isRowEmpty =
    totalRows === 1 &&
    !dosage.mealTime &&
    !dosage.whenToTake &&
    !dosage.supplementType;

  const getLabelByValue = (
    options: { value: string; label: string }[],
    value: string
  ) => options.find((o) => o.value === value)?.label || value;

  if (!viewMode && showDeleteConfirm) {
    return (
      <div className="flex items-center justify-between px-3 py-[6px] bg-[#FFF5F5] border border-[#FFCCC9] rounded-md">
        <span className="text-sm 3xl:text-base text-[#1F1F1F] truncate mr-4">
          Deleting{" "}
          <span className="font-medium">
            "{dosage.supplementType || "this dosage"}"
          </span>
          ?
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="danger"
            size="xs"
            onClick={() => {
              onRemoveDosage(supplementId, dosage.id);
              setShowDeleteConfirm(false);
            }}
            className="whitespace-nowrap"
          >
            Confirm delete
          </Button>
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors p-1 rounded border-[0.5px] border-[#1F1F1F]"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  if (viewMode) {
    const timeDisplay =
      dosage.consumeHour && dosage.consumeMinute && dosage.consumePeriod
        ? `${dosage.consumeHour}:${dosage.consumeMinute} ${dosage.consumePeriod}`
        : "—";

    return (
      <div className="grid grid-cols-[2fr_2fr_2fr_0.6fr_2fr] gap-5 items-center">
        <ReadOnlyField value={getLabelByValue(mealTimeOptions, dosage.mealTime)} />
        <ReadOnlyField value={getLabelByValue(whenToTakeOptions, dosage.whenToTake)} />
        <ReadOnlyField value={getLabelByValue(supplementTypeOptions, dosage.supplementType)} />
        <ReadOnlyField value={dosage.dosage} />
        <ReadOnlyField value={timeDisplay} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[2fr_2fr_2fr_0.6fr_2fr_auto] gap-5 items-center">
      {/* Meal time */}
      <CustomDropdown
        options={mealTimeOptions}
        value={dosage.mealTime}
        onChange={(v) => onDosageChange(supplementId, dosage.id, "mealTime", v)}
        placeholder="Select time"
        searchable={false}
      />

      {/* When to take */}
      <CustomDropdown
        options={whenToTakeOptions}
        value={dosage.whenToTake}
        onChange={(v) =>
          onDosageChange(supplementId, dosage.id, "whenToTake", v)
        }
        placeholder="Select type"
        searchable={false}
      />

      {/* Supplement type */}
      <CustomDropdown
        options={supplementTypeOptions}
        value={dosage.supplementType}
        onChange={(v) =>
          onDosageChange(supplementId, dosage.id, "supplementType", v)
        }
        placeholder="Select type"
        searchable={false}
      />

      {/* Dosage count */}
      <CustomDropdown
        options={dosageCountOptions}
        value={dosage.dosage}
        onChange={(v) => onDosageChange(supplementId, dosage.id, "dosage", v)}
        placeholder="1"
        searchable={false}
      />

      {/* Intake time Hr / Min / Period */}
      <div className="grid grid-cols-3 gap-2">
        <CustomDropdown
          options={hourOptions}
          value={dosage.consumeHour}
          onChange={(v) =>
            onDosageChange(supplementId, dosage.id, "consumeHour", v)
          }
          placeholder="Hr"
          searchable={false}
        />
        <CustomDropdown
          options={minuteOptions}
          value={dosage.consumeMinute}
          onChange={(v) =>
            onDosageChange(supplementId, dosage.id, "consumeMinute", v)
          }
          placeholder="Min"
          searchable={false}
        />
        <CustomDropdown
          options={periodOptions}
          value={dosage.consumePeriod}
          onChange={(v) =>
            onDosageChange(supplementId, dosage.id, "consumePeriod", v)
          }
          placeholder="AM"
          searchable={false}
        />
      </div>

      {/* Delete button */}
      <div className="flex items-center justify-center">
        <button
          onClick={() => (isRowEmpty ? undefined : setShowDeleteConfirm(true))}
          disabled={isRowEmpty}
          title={isRowEmpty ? "Nothing to delete" : "Delete dosage"}
          className={`transition-colors ${
            isRowEmpty
              ? "text-[#D1D1D6] cursor-not-allowed"
              : "text-[#FF3B30] hover:text-red-700 cursor-pointer"
          }`}
        >
          <DeleteIcon className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>
    </div>
  );
};

// ── SupplementCard ────────────────────────────────────────────────────────────
interface SupplementCardProps {
  supplement: SupplementEntry;
  totalSupplements: number;
  viewMode?: boolean;
  onSupplementChange: (id: string, field: string, value: string) => void;
  onDosageChange: (
    supplementId: string,
    dosageId: string,
    field: string,
    value: string
  ) => void;
  onAddDosage: (supplementId: string) => void;
  onRemoveDosage: (supplementId: string, dosageId: string) => void;
  onRemoveSupplement: (id: string) => void;
}

const SupplementCard: React.FC<SupplementCardProps> = ({
  supplement,
  totalSupplements,
  viewMode = false,
  onSupplementChange,
  onDosageChange,
  onAddDosage,
  onRemoveDosage,
  onRemoveSupplement,
}) => {
  useCustomScrollbar();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const isOnlyCard = totalSupplements === 1;
  const isWeekly = supplement.frequency === "Weekly";

  const getLabelByValue = (
    options: { value: string; label: string }[],
    value: string
  ) => options.find((o) => o.value === value)?.label || value;

  return (
    <div className="bg-[#FCFCFC] rounded-md border-[0.5px] border-[#F3F4F6] p-4 space-y-4">
      {/* ── Top fields ── */}
      <div className={`grid gap-5 ${isWeekly ? "grid-cols-6" : "grid-cols-5"}`}>
        {/* Name */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Name
          </label>
          {viewMode ? (
            <ReadOnlyField value={supplement.name} placeholder="No name" />
          ) : (
            <input
              value={supplement.name}
              onChange={(e) =>
                onSupplementChange(supplement.id, "name", e.target.value)
              }
              placeholder="Enter supplement name"
              className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base bg-white focus:outline-none focus:border-[#0DB5B5] placeholder:text-[#C7C7CC] text-[#1F1F1F] transition-colors"
            />
          )}
        </div>

        {/* Salt name */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Salt name
          </label>
          {viewMode ? (
            <ReadOnlyField value={supplement.saltName} />
          ) : (
            <input
              value={supplement.saltName}
              onChange={(e) =>
                onSupplementChange(supplement.id, "saltName", e.target.value)
              }
              placeholder="Enter value"
              className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base bg-white focus:outline-none focus:border-[#0DB5B5] placeholder:text-[#C7C7CC] text-[#1F1F1F] transition-colors"
            />
          )}
        </div>

        {/* Strength */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Strength
          </label>
          {viewMode ? (
            <ReadOnlyField value={supplement.strength} />
          ) : (
            <input
              value={supplement.strength}
              onChange={(e) =>
                onSupplementChange(supplement.id, "strength", e.target.value)
              }
              placeholder="Enter value"
              className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base bg-white focus:outline-none focus:border-[#0DB5B5] placeholder:text-[#C7C7CC] text-[#1F1F1F] transition-colors"
            />
          )}
        </div>

        {/* Frequency */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Frequency
          </label>
          {viewMode ? (
            <ReadOnlyField
              value={getLabelByValue(frequencyOptions, supplement.frequency)}
            />
          ) : (
            <CustomDropdown
              options={frequencyOptions}
              value={supplement.frequency}
              onChange={(v) => {
                onSupplementChange(supplement.id, "frequency", v);
                // Clear dayOfWeek when switching away from Weekly
                if (v !== "Weekly") {
                  onSupplementChange(supplement.id, "dayOfWeek", "");
                }
              }}
              placeholder="Select type"
              searchable={false}
            />
          )}
        </div>

        {/* Choose one day of the week — only shown when frequency is Weekly */}
        {isWeekly && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
              Choose a day of the week
            </label>
            {viewMode ? (
              <ReadOnlyField
                value={getLabelByValue(dayOfWeekOptions, supplement.dayOfWeek)}
                placeholder="—"
              />
            ) : (
              <CustomDropdown
                options={dayOfWeekOptions}
                value={supplement.dayOfWeek}
                onChange={(v) =>
                  onSupplementChange(supplement.id, "dayOfWeek", v)
                }
                placeholder="Select day"
                searchable={false}
              />
            )}
          </div>
        )}

        {/* Consume with */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Consume with
          </label>
          {viewMode ? (
            <ReadOnlyField
              value={getLabelByValue(
                consumeWithTopOptions,
                supplement.consumeWith
              )}
            />
          ) : (
            <CustomDropdown
              options={consumeWithTopOptions}
              value={supplement.consumeWith}
              onChange={(v) =>
                onSupplementChange(supplement.id, "consumeWith", v)
              }
              placeholder="Select type"
              searchable={false}
            />
          )}
        </div>
      </div>

      {/* ── Dosage section ── */}
      <div>
        {/* Column headers */}
        <div
          className={`grid gap-5 mb-2 ${
            viewMode
              ? "grid-cols-[2fr_2fr_2fr_0.6fr_2fr]"
              : "grid-cols-[2fr_2fr_2fr_0.6fr_2fr_auto]"
          }`}
        >
          <span className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Meal time
          </span>
          <span className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            When to take
          </span>
          <span className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Supplement type
          </span>
          <span className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Dosage
          </span>
          <span className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Intake time
          </span>
          {!viewMode && <div className="w-5" />}
        </div>

        {/* Dosage rows */}
        <div className="space-y-3">
          {supplement.dosages.map((dosage) => (
            <DosageRow
              key={dosage.id}
              dosage={dosage}
              totalRows={supplement.dosages.length}
              supplementId={supplement.id}
              viewMode={viewMode}
              onDosageChange={onDosageChange}
              onRemoveDosage={onRemoveDosage}
            />
          ))}
        </div>

        {/* Add more dosage — hidden in view mode */}
        {!viewMode && (
          <button
            onClick={() => onAddDosage(supplement.id)}
            className="flex items-center gap-1.5 text-[#0DB5B5] text-sm 3xl:text-base font-medium hover:text-[#0CA0A0] transition-colors mt-3"
          >
            <span className="text-base leading-none">+</span>
            <span>Add more dosage</span>
          </button>
        )}
      </div>

      {/* ── Remove card — hidden in view mode ── */}
      {!viewMode && !isOnlyCard && (
        <div className="pt-2 border-t border-[#E0E0E0] flex justify-end">
          {showRemoveConfirm ? (
            <div className="flex items-center gap-2">
              <span className="text-sm 3xl:text-base text-[#1F1F1F]">
                Remove this supplement?
              </span>
              <Button
                variant="danger"
                size="xs"
                onClick={() => onRemoveSupplement(supplement.id)}
              >
                Confirm
              </Button>
              <Button
                variant="secondary"
                size="xs"
                onClick={() => setShowRemoveConfirm(false)}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <button
              onClick={() => setShowRemoveConfirm(true)}
              className="flex items-center gap-1.5 text-[#FF3B30] text-sm 3xl:text-base font-medium hover:text-red-700 transition-colors"
            >
              <DeleteIcon className="w-4 h-4" />
              <span>Remove supplement</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Popup ────────────────────────────────────────────────────────────────
const SupplementPlanCreatePopup: React.FC<SupplementPlanCreatePopupProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userId,
  viewMode = false,
  initialData = null,
  onShowToast,
}) => {
  const { user } = useAuth();
  const nutristId = user?.nutrist_id;

  const [isCreating, setIsCreating] = useState(false);
  const [supplements, setSupplements] = useState<SupplementEntry[]>([
    newEmptySupplement(),
  ]);

  // Populate supplements from initialData when in view mode
  React.useEffect(() => {
    if (isOpen) {
      if (viewMode && initialData?.supplements?.length) {
        setSupplements(initialData.supplements);
      } else if (!viewMode) {
        setSupplements([newEmptySupplement()]);
      }
    }
  }, [isOpen, viewMode, initialData]);

  // ── Supplement-level handlers ──────────────────────────────────────────────
  const handleSupplementChange = (id: string, field: string, value: string) => {
    setSupplements((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  const handleAddSupplement = () => {
    setSupplements((prev) => [...prev, newEmptySupplement()]);
  };

  const handleRemoveSupplement = (id: string) => {
    setSupplements((prev) => {
      const remaining = prev.filter((s) => s.id !== id);
      return remaining.length === 0 ? [newEmptySupplement()] : remaining;
    });
  };

  // ── Dosage-level handlers ──────────────────────────────────────────────────
  const handleDosageChange = (
    supplementId: string,
    dosageId: string,
    field: string,
    value: string
  ) => {
    setSupplements((prev) =>
      prev.map((s) => {
        if (s.id !== supplementId) return s;
        return {
          ...s,
          dosages: s.dosages.map((d) =>
            d.id === dosageId ? { ...d, [field]: value } : d
          ),
        };
      })
    );
  };

  const handleAddDosage = (supplementId: string) => {
    setSupplements((prev) =>
      prev.map((s) =>
        s.id === supplementId
          ? { ...s, dosages: [...s.dosages, newEmptyDosage()] }
          : s
      )
    );
  };

  const handleRemoveDosage = (supplementId: string, dosageId: string) => {
    setSupplements((prev) =>
      prev.map((s) => {
        if (s.id !== supplementId) return s;
        const remaining = s.dosages.filter((d) => d.id !== dosageId);
        return {
          ...s,
          dosages: remaining.length === 0 ? [newEmptyDosage()] : remaining,
        };
      })
    );
  };

  // ── Validation ─────────────────────────────────────────────────────────────
const isFormComplete = () =>
  supplements.every((s) => {
    const supplementValid =
      s.name.trim() !== "" &&
      s.strength.trim() !== "" &&
      s.frequency !== "" &&
      (s.frequency !== "Weekly" || s.dayOfWeek !== "") &&
      s.consumeWith !== "";

    const dosagesValid = s.dosages.every(
      (d) =>
        d.mealTime !== "" &&
        d.whenToTake !== "" &&
        d.supplementType !== "" &&
        d.dosage !== "" &&
        d.consumeHour !== "" &&
        d.consumeMinute !== "" &&
        d.consumePeriod !== ""
    );

    return supplementValid && dosagesValid;
  });

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSendToClient = async () => {
    if (!isFormComplete()) {
      onShowToast?.(
        "warning",
        "Incomplete form",
        "Please enter a supplement name for all entries"
      );
      return;
    }

    setIsCreating(true);

    try {
      // Build API payload mapping local state → API shape
      const payload = {
        fk_admin_id: null,
        fk_user_id: String(userId),
        fk_nutritionist_id: String(nutristId),
        supplement_status: "2",
        sets: supplements.map((s) => ({
          medicine_name: s.name,
          salt_name: s.saltName,
          strength: s.strength,
          frequency: s.frequency,
          weekly_day: s.frequency === "Weekly" ? s.dayOfWeek : null,
          consume_with: s.consumeWith,
          details: s.dosages.map((d) => ({
            meal_type: d.mealTime,
            before_after_meal: d.whenToTake.replace(" meal", ""),
            dosage: d.dosage,
            dosage_type: d.supplementType,
            minutes_before_after_meal_hr: d.consumeHour || "00",
            minutes_before_after_meal_min: d.consumeMinute || "00",
            am_pm: d.consumePeriod || "AM",
            detail_id: null,
          })),
        })),
      };

      const json = await addSupplement(payload);

      if (json.success === "1") {
        sendPlanNotificationToUser(userId, "supplement_plan");

        const newSupplementPlan = {
          id: Date.now(),
          name: supplements[0].name,
          created_by: "You",
          created_on: new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
          status: "Active" as const,
          supplements,
        };

        onSubmit(newSupplementPlan);
        handleClose();
        onShowToast?.(
          "success",
          "Supplement plan created",
          json.message || "Your supplement plan has been successfully created"
        );
      } else {
        onShowToast?.(
          "error",
          "Failed to create plan",
          json.message || "Something went wrong. Please try again."
        );
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      onShowToast?.("error", "Error", message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setSupplements([newEmptySupplement()]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-7xl overflow-hidden flex flex-col max-h-[80vh]">
        {/* ── Header ── */}
        <div className="pt-5 py-3 mx-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F]">
              {viewMode ? "View supplement plan" : "Create supplement plan"}
            </h2>
            {/* Status badge in view mode */}
            {viewMode && initialData?.status && (
              <span
                className={`px-3 py-1 rounded-[24px] text-xs 3xl:text-sm font-medium ${
                  initialData.status === "Active"
                    ? "bg-[#E5F8F8] text-[#0DB5B5]"
                    : initialData.status === "Approval Pending"
                    ? "bg-[#FFF4E5] text-[#FF9500]"
                    : initialData.status === "In-Active"
                    ? "bg-[#F0F0F0] text-[#8E8E93]"
                    : "bg-[#F0F0F0] text-[#646464]"
                }`}
              >
                {initialData.status}
              </span>
            )}
          </div>

          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto custom-scrollbar-overlay px-6 py-5 space-y-4">
          {supplements.map((supplement) => (
            <SupplementCard
              key={supplement.id}
              supplement={supplement}
              totalSupplements={supplements.length}
              viewMode={viewMode}
              onSupplementChange={handleSupplementChange}
              onDosageChange={handleDosageChange}
              onAddDosage={handleAddDosage}
              onRemoveDosage={handleRemoveDosage}
              onRemoveSupplement={handleRemoveSupplement}
            />
          ))}

          {/* Add more supplement — hidden in view mode */}
          {!viewMode && (
            <button
              onClick={handleAddSupplement}
              className="flex items-center gap-1.5 text-[#0DB5B5] text-sm 3xl:text-base font-medium hover:text-[#0CA0A0] transition-colors"
            >
              <span className="text-base leading-none">+</span>
              <span>Add more</span>
            </button>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="py-4 mx-6 border-t border-gray-200 flex items-center justify-end gap-4 flex-shrink-0">
          {viewMode ? (
            // View mode: just a close button
            <Button
              variant="secondary"
              size="md"
              onClick={handleClose}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                size="md"
                onClick={handleClose}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSendToClient}
                disabled={isCreating || !isFormComplete()}
              >
                {isCreating && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {isCreating ? "Sending..." : "Send to client"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplementPlanCreatePopup;